import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown } from "lucide-react";
import { HERO_IMG, PHONE_TEL, PHONE_DISPLAY, TAGLINE } from "../../data/menu";

const lineParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.35 } },
};
const lineChild = {
  hidden: { y: "110%" },
  show: {
    y: "0%",
    transition: { duration: 1, ease: [0.16, 1, 0.3, 1] },
  },
};

const Line = ({ children, className }) => (
  <span className="reveal-mask">
    <motion.span variants={lineChild} className={`block ${className || ""}`}>
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      ref={ref}
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden pt-24 md:pt-28"
    >
      {/* Parallax food image, spotlight-clipped bottom right */}
      <motion.div
        style={{ y: imgY, scale: imgScale }}
        className="pointer-events-none absolute -right-10 bottom-0 h-[46vh] w-[75vw] md:h-[72vh] md:w-[42vw] z-0"
      >
        <div className="absolute inset-0 spotlight blur-2xl scale-125" />
        <div className="relative h-full w-full overflow-hidden rounded-t-[999px] border border-white/10">
          <img
            src={HERO_IMG}
            alt="Fresh catering cooked to order"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
        </div>
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 mx-auto max-w-[1600px] px-5 md:px-10"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="font-body uppercase tracking-[0.4em] text-[#ffb703] text-xs md:text-sm mb-4 md:mb-8"
        >
          Home Catering • Cooked To Order
        </motion.p>

        <motion.h1
          variants={lineParent}
          initial="hidden"
          animate="show"
          data-testid="hero-heading"
          className="font-display uppercase leading-[0.82] tracking-tight text-[18vw] md:text-[13vw]"
        >
          <Line className="text-white">T Will</Line>
          <Line className="text-[#e63946]">Cater</Line>
          <Line className="text-white">
            2 <span className="text-[#ffb703]">U</span>
          </Line>
        </motion.h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-8 md:mt-12 flex flex-col md:flex-row md:items-end gap-6 md:gap-16"
        >
          <p className="font-body text-lg md:text-2xl text-[#f8f9fa] max-w-md leading-relaxed">
            {TAGLINE} Spaghetti, wings & beans — made fresh and brought straight
            to your table.
          </p>

          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="hero-call-button"
            className="group inline-flex items-center gap-3 rounded-full bg-[#e63946] px-7 py-4 font-accent uppercase tracking-wide text-white text-lg transition-all duration-300 hover:bg-[#ffb703] hover:text-black hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#ffb703]"
          >
            <Phone className="h-5 w-5 transition-transform group-hover:rotate-12" />
            {PHONE_DISPLAY}
          </a>
        </motion.div>
      </motion.div>

      <motion.a
        href="#menu"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-6 left-5 md:left-10 z-10 flex items-center gap-3 font-body uppercase tracking-widest text-xs text-[#a8a8a8] hover:text-white transition-colors"
        data-testid="hero-scroll-cue"
      >
        <ArrowDown className="h-4 w-4 animate-bounce" />
        See the menu
      </motion.a>
    </section>
  );
};
